/* eslint-disable max-lines */
import { CloudUpload, FileText, Music, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import toast from 'react-hot-toast';

interface Props {
  alignmentMediaFile: File | null;
  alignmentTranscriptFile: File | null;
  uploading: boolean;
  alignmentProgress: number;
  onSetMediaFile: (file: File | null) => void;
  onSetTranscriptFile: (file: File | null) => void;
  validateMedia: (file: File) => { valid: boolean; error?: string };
  validateTranscript: (file: File) => { valid: boolean; error?: string };
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export function UploadAlignmentPanel({
  alignmentMediaFile,
  alignmentTranscriptFile,
  uploading,
  alignmentProgress,
  onSetMediaFile,
  onSetTranscriptFile,
  validateMedia,
  validateTranscript
}: Props) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  return (
    <div className="space-y-5 overflow-hidden">
      <p
        className="text-sm leading-relaxed"
        style={{ color: getColour('inactiveText') }}
      >
        Upload a media file and an existing transcript to align them and
        generate timestamped output.
      </p>

      {/* Media file drop zone */}
      <div
        className={cn(
          'relative border-2 border-dashed rounded-xl p-5 transition-all',
          'duration-200 hover:border-opacity-80',
          alignmentMediaFile ? 'border-solid' : 'hover:bg-opacity-50'
        )}
        style={{
          borderColor: alignmentMediaFile
            ? pick('rgb(34 197 94)', 'rgb(74 222 128)')
            : getColour('border'),
          backgroundColor: alignmentMediaFile
            ? pick('rgba(34, 197, 94, 0.05)', 'rgba(74, 222, 128, 0.05)')
            : 'transparent'
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{
              backgroundColor: pick(
                'rgba(99, 102, 241, 0.1)',
                'rgba(129, 140, 248, 0.1)'
              )
            }}
          >
            <Music
              className="w-5 h-5"
              style={{ color: pick('rgb(99, 102, 241)', 'rgb(129, 140, 248)') }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <label
              className="block text-sm font-semibold mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('media-file') || 'Media File'}
            </label>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('audio-or-video-file') || 'Audio or video file (max 2GB)'}
            </p>
            {!alignmentMediaFile ? (
              <label
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium cursor-pointer transition-all duration-200 hover:shadow-md"
                style={{
                  backgroundColor: pick(
                    'rgba(99, 102, 241, 0.1)',
                    'rgba(129, 140, 248, 0.15)'
                  ),
                  color: pick('rgb(99, 102, 241)', 'rgb(129, 140, 248)')
                }}
              >
                <CloudUpload className="w-4 h-4" />
                {t('choose-file') || 'Choose File'}
                <input
                  type="file"
                  accept="audio/*,video/*"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const validation = validateMedia(file);
                    if (!validation.valid)
                      return toast.error(validation.error || 'Invalid file');
                    onSetMediaFile(file);
                  }}
                  disabled={uploading}
                  className="sr-only"
                />
              </label>
            ) : (
              <div
                className="flex items-center gap-3 p-3 rounded-2xl"
                style={{
                  backgroundColor: pick(
                    'rgba(0, 0, 0, 0.03)',
                    'rgba(255, 255, 255, 0.03)'
                  )
                }}
              >
                <span
                  className="flex-1 text-sm font-medium truncate min-w-0"
                  title={alignmentMediaFile.name}
                  style={{ color: getColour('normalText') }}
                >
                  {alignmentMediaFile.name}
                </span>
                <span
                  className="text-xs whitespace-nowrap flex-shrink-0 px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: pick(
                      'rgba(0, 0, 0, 0.05)',
                      'rgba(255, 255, 255, 0.08)'
                    ),
                    color: getColour('inactiveText')
                  }}
                >
                  {formatFileSize(alignmentMediaFile.size)}
                </span>
                <button
                  onClick={() => onSetMediaFile(null)}
                  disabled={uploading}
                  className="flex-shrink-0 p-1 rounded-full transition-all duration-200 hover:bg-red-500/10"
                  style={{ color: pick('rgb(239 68 68)', 'rgb(248 113 113)') }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transcript file drop zone */}
      <div
        className={cn(
          'relative border-2 border-dashed rounded-xl p-5 transition-all',
          'duration-200 hover:border-opacity-80',
          alignmentTranscriptFile ? 'border-solid' : 'hover:bg-opacity-50'
        )}
        style={{
          borderColor: alignmentTranscriptFile
            ? pick('rgb(34 197 94)', 'rgb(74 222 128)')
            : getColour('border'),
          backgroundColor: alignmentTranscriptFile
            ? pick('rgba(34, 197, 94, 0.05)', 'rgba(74, 222, 128, 0.05)')
            : 'transparent'
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{
              backgroundColor: pick(
                'rgba(245, 158, 11, 0.1)',
                'rgba(251, 191, 36, 0.1)'
              )
            }}
          >
            <FileText
              className="w-5 h-5"
              style={{ color: pick('rgb(245, 158, 11)', 'rgb(251, 191, 36)') }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <label
              className="block text-sm font-semibold mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('transcript-file') || 'Transcript File'}
            </label>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('txt-vtt-srt-sbv') || 'TXT, VTT, SRT, or SBV file (max 10MB)'}
            </p>
            {!alignmentTranscriptFile ? (
              <label
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium cursor-pointer transition-all duration-200 hover:shadow-md"
                style={{
                  backgroundColor: pick(
                    'rgba(245, 158, 11, 0.1)',
                    'rgba(251, 191, 36, 0.15)'
                  ),
                  color: pick('rgb(245, 158, 11)', 'rgb(251, 191, 36)')
                }}
              >
                <CloudUpload className="w-4 h-4" />
                {t('choose-file') || 'Choose File'}
                <input
                  type="file"
                  accept=".txt,.vtt,.srt,.sbv"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const validation = validateTranscript(file);
                    if (!validation.valid)
                      return toast.error(validation.error || 'Invalid file');
                    onSetTranscriptFile(file);
                  }}
                  disabled={uploading}
                  className="sr-only"
                />
              </label>
            ) : (
              <div
                className="flex items-center gap-3 p-3 rounded-2xl"
                style={{
                  backgroundColor: pick(
                    'rgba(0, 0, 0, 0.03)',
                    'rgba(255, 255, 255, 0.03)'
                  )
                }}
              >
                <span
                  className="flex-1 text-sm font-medium truncate min-w-0"
                  title={alignmentTranscriptFile.name}
                  style={{ color: getColour('normalText') }}
                >
                  {alignmentTranscriptFile.name}
                </span>
                <span
                  className="text-xs whitespace-nowrap flex-shrink-0 px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: pick(
                      'rgba(0, 0, 0, 0.05)',
                      'rgba(255, 255, 255, 0.08)'
                    ),
                    color: getColour('inactiveText')
                  }}
                >
                  {formatFileSize(alignmentTranscriptFile.size)}
                </span>
                <button
                  onClick={() => onSetTranscriptFile(null)}
                  disabled={uploading}
                  className="flex-shrink-0 p-1 rounded-full transition-all duration-200 hover:bg-red-500/10"
                  style={{ color: pick('rgb(239 68 68)', 'rgb(248 113 113)') }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress */}
      {uploading && alignmentProgress > 0 && (
        <div className="mt-4 px-1">
          <div className="flex items-center justify-between mb-2">
            <span
              className="text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('uploading') || 'Uploading'}...
            </span>
            <span
              className="text-xs font-semibold"
              style={{ color: pick('rgb(34 197 94)', 'rgb(74 222 128)') }}
            >
              {alignmentProgress}%
            </span>
          </div>
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: getColour('border') }}
          >
            <div
              className="h-full rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${alignmentProgress}%`,
                backgroundColor: pick('rgb(34 197 94)', 'rgb(74 222 128)'),
                boxShadow: `0 0 8px ${pick('rgba(34, 197, 94, 0.4)', 'rgba(74, 222, 128, 0.4)')}`
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
